import { useState } from "react";
import { Lock, Loader2, Download, Users, Crown, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Admin() {
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signups, setSignups] = useState([]);
  const [stats, setStats] = useState(null);

  const load = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        api.get("/signups", { params: { code } }),
        api.get("/signups/stats", { params: { code } }),
      ]);
      setSignups(list.data);
      setStats(s.data);
      setAuthed(true);
    } catch (err) {
      toast.error("Invalid access code.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Phone", "Interest", "Message", "Date"],
      ...signups.map((s) => [
        s.name,
        s.email,
        s.phone || "",
        s.interest,
        (s.message || "").replace(/,/g, " "),
        new Date(s.created_at).toLocaleString(),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "bnb-signups.csv";
    a.click();
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wine-deep px-6">
        <form
          onSubmit={load}
          data-testid="admin-login"
          className="w-full max-w-sm rounded-3xl border border-gold/20 bg-wine-surface/40 p-8"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gold mb-6">
            <Lock className="h-5 w-5 text-wine-deep" />
          </div>
          <h1 className="font-display text-3xl text-cream">Admin Access</h1>
          <p className="text-cream/50 text-sm mt-2 mb-6">Enter your access code to view signups.</p>
          <input
            data-testid="admin-code-input"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Access code"
            className="w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-3.5 text-cream placeholder-cream/30 outline-none focus:border-gold transition-colors duration-300"
          />
          <button
            data-testid="admin-login-btn"
            disabled={loading}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.18em] text-wine-deep hover:bg-cream transition-colors duration-300"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
          </button>
        </form>
      </div>
    );
  }

  const cards = [
    { label: "Total Signups", value: stats?.total ?? 0, Icon: Users },
    { label: "Enroll Interest", value: stats?.enroll ?? 0, Icon: Crown },
    { label: "Waitlist", value: stats?.waitlist ?? 0, Icon: ListChecks },
  ];

  return (
    <div className="min-h-screen bg-wine-deep px-6 py-12" data-testid="admin-dashboard">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <h1 className="font-display text-4xl text-cream">Signups Dashboard</h1>
            <p className="text-cream/50 mt-1">Crowned™ Pro Level 1 — leads captured</p>
          </div>
          <button
            data-testid="export-csv-btn"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-wine-deep hover:bg-cream transition-colors duration-300"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl border border-gold/15 bg-wine-surface/40 p-6">
              <c.Icon className="h-6 w-6 text-gold mb-4" />
              <div className="font-display text-5xl text-cream">{c.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">
                {c.label}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-gold/15 overflow-hidden">
          <table className="w-full text-left" data-testid="signups-table">
            <thead className="bg-wine-surface/60">
              <tr className="font-mono text-[10px] uppercase tracking-[0.15em] text-cream/50">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Phone</th>
                <th className="px-5 py-4">Interest</th>
                <th className="px-5 py-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {signups.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-cream/40">
                    No signups yet.
                  </td>
                </tr>
              ) : (
                signups.map((s) => (
                  <tr key={s.id} className="border-t border-cream/10 text-cream/80">
                    <td className="px-5 py-4">{s.name}</td>
                    <td className="px-5 py-4 text-cream/60">{s.email}</td>
                    <td className="px-5 py-4 text-cream/60">{s.phone || "—"}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`font-mono text-[10px] uppercase tracking-[0.1em] rounded-full px-3 py-1 ${
                          s.interest === "enroll"
                            ? "bg-gold/20 text-gold"
                            : "bg-cream/10 text-cream/60"
                        }`}
                      >
                        {s.interest}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-cream/50 text-sm">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
